import React, { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import AddressForm from 'components/AddressForm'
import BrandedButton from 'components/BrandedButton'
import BackButton from 'components/BackButton'
import ChoiceField from 'components/form/ChoiceField'
import FullNameField from 'components/form/FullNameField'
import withInputAnalytics from 'hoc/WithInputAnalytics'
import { STAGE_PLAN_SELECT } from 'containers/Checkout/constants'
import { transitionToStage } from 'containers/Checkout/actions'
import { validateAddressForm } from 'utilities/formValidator.util'

import {
  ATTENTION_TO_KEY,
  GA_CATEGORY,
  MODEM_DISCLAIMER,
  REQUIRES_MODEM,
  SAME_AS_INSTALL_ADDRESS,
} from './constants'

import {
  setDeliverySameAsInstall,
  setManualAddress,
  setModemRequired,
  setFieldValues,
  setFieldErrors,
  submitData,
} from './actions'
import styles from './styles.scss'

// Add form analytics to fields
const TrackedFullNameField = withInputAnalytics(GA_CATEGORY)(FullNameField)
const TrackedChoiceField = withInputAnalytics(GA_CATEGORY)(ChoiceField)

export class ModemStageComponent extends Component {
  static propTypes = {
    isLoading: PropTypes.bool,
    transitionToStage: PropTypes.func,

    // Modem settings from Wagtail
    modemSettings: PropTypes.shape({
      disclaimer: PropTypes.string,
    }),

    // redux state
    fieldValues: PropTypes.object,
    fieldErrors: PropTypes.object,
    modemRequired: PropTypes.bool,
    modemResponseMessage: PropTypes.string,
    sameAsInstallAddress: PropTypes.bool,
    manualAddress: PropTypes.bool,
    installAddress: PropTypes.arrayOf(PropTypes.string),

    // actions
    setDeliverySameAsInstall: PropTypes.func,
    setManualAddress: PropTypes.func,
    setModemRequired: PropTypes.func,
    setFieldValues: PropTypes.func,
    setFieldErrors: PropTypes.func,
    submitData: PropTypes.func,
  }

  static defaultProps = {

    modemSettings: {
      disclaimer: MODEM_DISCLAIMER,
    },

    // redux state
    isLoading: false,
    fieldValues: {},
    fieldErrors: {},
    modemRequired: false,
    modemResponseMessage: '',
    sameAsInstallAddress: true,
    manualAddress: false,
    installAddress: [],

    // actions
    transitionToStage: f => f,
    setDeliverySameAsInstall: f => f,
    setManualAddress: f => f,
    setModemRequired: f => f,
    setFieldValues: f => f,
    setFieldErrors: f => f,
    submitData: f => f,
  }

  componentWillUnmount() {
    // When moving to next step, scroll position is below fold
    window.scrollTo(0, 0)
  }

  onRequireModemChange = (event) => {
    this.props.setModemRequired(event.target.value === 'true')
  }

  onAddressOptionChange = (event) => {
    this.props.setDeliverySameAsInstall(event.target.value === 'true')
  }

  onFullNameChange = (event) => {
    this.props.setFieldValues({ [event.target.name]: event.target.value })
  }

  onSubmitForm = (event) => {
    event.preventDefault()

    const valid = this.validate()
    if (valid !== true) {
      this.props.setFieldErrors(valid)
      return
    }
    this.props.submitData()
  }

  onBack = (event) => {
    event.preventDefault()
    this.props.transitionToStage(STAGE_PLAN_SELECT)
  }

  validate = () => {
    const {
      sameAsInstallAddress,
      modemRequired,
      fieldValues,
      manualAddress,
    } = this.props

    if (modemRequired && !sameAsInstallAddress) {
      return validateAddressForm(fieldValues, manualAddress)
    }
    return true
  }

  renderModemChoice = () => {
    const {
      isLoading,
      modemSettings,
      modemRequired,
    } = this.props

    return (
      <Fragment>
        <TrackedChoiceField
          name={REQUIRES_MODEM}
          value={modemRequired}
          onChange={this.onRequireModemChange}
          loading={isLoading}
          required
        />
        { !modemRequired ?
          <Fragment>
            <div title="Modem Compatibility Information">
              { // eslint-disable-next-line react/no-danger, html contains entities
              } <div dangerouslySetInnerHTML={{ __html: modemSettings.disclaimer }} />
            </div>
          </Fragment>
          : null
        }

      </Fragment>
    )
  }

  renderAddressChoice = () => {
    const {
      isLoading,
      installAddress,
      modemRequired,
      sameAsInstallAddress,
    } = this.props

    if (!modemRequired) return null

    const address = (
      <div>
        {installAddress.length >= 1 && <div>{installAddress[0]},</div>}
        {installAddress.length >= 2 &&
          <div className={styles.addressLine2}>{installAddress[1]}</div>}
      </div>
    )

    const diffAddress = <div>Use a different address</div>

    const options = [
      { label: address, value: true },
      { label: diffAddress, value: false },
    ]

    return (
      <Fragment>
        <span>Where should we deliver your modem to?</span>
        <TrackedChoiceField
          name={SAME_AS_INSTALL_ADDRESS}
          options={options}
          value={sameAsInstallAddress}
          onChange={this.onAddressOptionChange}
          loading={isLoading}
          required
        />
      </Fragment>
    )
  }

  renderAddressSelection = () => {
    const {
      modemRequired,
      sameAsInstallAddress,
      manualAddress,
      isLoading,
      fieldValues,
      fieldErrors,
    } = this.props

    if (!modemRequired) return null
    if (sameAsInstallAddress) return null

    return (
      <div className={styles.modemAddress}>
        <TrackedFullNameField
          name={ATTENTION_TO_KEY}
          onChange={this.onFullNameChange}
          onBlur={this.onFullNameChange}
          value={fieldValues[ATTENTION_TO_KEY]}
          errorMessage={fieldErrors[ATTENTION_TO_KEY]}
          loading={isLoading}
          required
        />
        <AddressForm
          loading={isLoading}
          manual={manualAddress}
          fieldErrors={fieldErrors}
          fieldValues={fieldValues}
          onAddressSelected={this.props.setFieldValues}
          onFieldUpdated={this.props.setFieldValues}
          onManualEntry={this.props.setManualAddress}
          gaCategory={GA_CATEGORY}
        />
      </div>
    )
  }

  renderResponseBlock = () => {
    const { modemResponseMessage } = this.props
    return (
      modemResponseMessage
        ? <p className={styles.form_error}>{modemResponseMessage}</p>
        : null
    )
  }

  renderButtons = () => (
    <div className={styles.btnWrap}>
      <BrandedButton
        className={styles.submitBtn}
        labelText="Continue"
        iconName="arrowRight"
        fitContent
      />
      <BackButton onClick={this.onBack} />
    </div>
  )

  render() {
    const modemChoiceBlock = this.renderModemChoice()
    const addressChoiceBlock = this.renderAddressChoice()
    const addressBlock = this.renderAddressSelection()
    const buttonBlock = this.renderButtons()
    const responseBlock = this.renderResponseBlock()

    return (
      <div className={styles.container}>
        <h2>Modem</h2>
        <form
          className={styles.formContainer}
          onSubmit={this.onSubmitForm}
          method="POST"
        >
          <fieldset>
            {modemChoiceBlock}
            {addressChoiceBlock}
            {addressBlock}
            {buttonBlock}
            {responseBlock}
          </fieldset>
        </form>
      </div>
    )
  }
}

function mapStateToProps(state) {
  const {
    isLoading,
    modemRequired,
    modemSameAsInstallAddress,
    modemManualAddress,
    modemResponseMessage,
    modemFields,
    modemErrors,
    installAddress,
  } = state.checkoutReducer

  return {
    isLoading,
    modemRequired,
    modemResponseMessage,
    installAddress,
    fieldValues: modemFields,
    fieldErrors: modemErrors,
    manualAddress: modemManualAddress,
    sameAsInstallAddress: modemSameAsInstallAddress,
  }
}

// Anything returned from this function will end up as props
function mapDispatchToProps(dispatch) {
  // bind dispatch to action creators which are then passed through props
  return bindActionCreators({
    setDeliverySameAsInstall,
    setManualAddress,
    setModemRequired,
    setFieldValues,
    setFieldErrors,
    submitData,
    transitionToStage,
  }, dispatch)
}

export default connect(mapStateToProps, mapDispatchToProps)(ModemStageComponent)



// WEBPACK FOOTER //
// ./containers/stages/ModemStage/index.jsx